import { Col, Container, Typography } from '@ag1/ui';
import clsx from 'clsx';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import ActivateSubscriptionModalV2 from 'shared/components/ActivateSubscriptionModalV2/ActivateSubscriptionModalV2';
import { HomeBanners } from 'shared/components/HomeBanners/HomeBanners';
import { SubscriptionMainCardV2 } from 'shared/components/SubscriptionMainCardV2';
import { useIsEnabled } from 'shared/hooks/useIsEnabled';
import Layout from 'shared/layouts/BaseLayout';
import { ReferFriendCard } from 'shared/ui/ReferFriendCard/ReferFriendCard';
import { Feature } from 'shared/utils/featureToggle';
import { useMedia } from 'state/context/MediaContext';
import { SubscriptionDetailsV2Response } from 'state/internal/getSubscriptionDetails/types';
import { EmptyState } from './EmptyState';
import style from './Home.module.scss';

interface HomeProps {
  subscriptions: SubscriptionDetailsV2Response[];
}

const Home = ({ subscriptions = [] }: HomeProps) => {
  const { t } = useTranslation(['banners', 'subscriptionList', 'commonContent']);
  const subscriptionCardContent = t('subscriptionList:subscriptionCard', { returnObjects: true });
  const referFriendCardContent = t('commonContent:referFriendCard', { returnObjects: true });

  const cancelledSubscriptions = useMemo(
    () => subscriptions?.filter((i) => !i.is_active) || [],
    [subscriptions]
  );

  const activeSubscriptions = useMemo(
    () =>
      subscriptions
        ?.filter((i) => i.is_active)
        .sort((a, b) => a.next_order_date - b.next_order_date) || [],
    [subscriptions]
  );

  const { atSmall } = useMedia();
  const hasActiveSubscriptions = !!activeSubscriptions.length;
  const hasCancelledSubscriptions = !!cancelledSubscriptions.length;

  const isReferFriendsEnabled = useIsEnabled(Feature.referFriends);

  const renderActiveSubscriptions = useCallback(() => {
    if (hasActiveSubscriptions) {
      return (
        <div
          className={clsx(
            style.subscriptionList,
            hasActiveSubscriptions && style.subscriptionListV2
          )}>
          <Container className={!atSmall ? style.customContainer : undefined}>
            <section className={style.section}>
              {activeSubscriptions?.map((subscription) => (
                <SubscriptionMainCardV2
                  key={subscription?.id}
                  subscription={subscription}
                  pageContent={subscriptionCardContent}
                />
              ))}
            </section>
          </Container>
        </div>
      );
    }

    return null;
  }, [hasActiveSubscriptions, atSmall, activeSubscriptions, subscriptionCardContent]);

  const renderCancelledSubscriptions = useCallback(() => {
    if (hasCancelledSubscriptions) {
      return (
        <div
          className={clsx(
            style.subscriptionList,
            hasCancelledSubscriptions && style.cancelledSubscriptionList
          )}>
          <Container>
            <div className={style.cancelContent}>
              <Typography
                variant="headlineSans"
                size="2"
                component="h2"
                color="black-new"
                noPadding>
                {t('subscriptionList:cancelList.title')}
              </Typography>
              <Col span={{ m: 9, l: 6 }} noGutter>
                <Typography variant="portalText" size="1" color="black-new" noPadding>
                  {t('subscriptionList:cancelList.description')}
                </Typography>
              </Col>
            </div>
          </Container>
          <Container className={!atSmall ? style.customContainer : undefined}>
            {hasCancelledSubscriptions && (
              <section className={clsx(style.section, style.sectionPaddingTop)}>
                {cancelledSubscriptions.map((subscription) => (
                  <SubscriptionMainCardV2
                    key={subscription?.id}
                    subscription={subscription}
                    className={style.subscriptionBaseCard}
                    pageContent={subscriptionCardContent}
                  />
                ))}
              </section>
            )}
          </Container>
        </div>
      );
    }

    return null;
  }, [atSmall, cancelledSubscriptions, hasCancelledSubscriptions, subscriptionCardContent, t]);

  return (
    <Layout className={style.homeLayout}>
      <ActivateSubscriptionModalV2 />
      <HomeBanners subscriptionsList={activeSubscriptions} />
      {subscriptions.length === 0 && <EmptyState />}
      {renderActiveSubscriptions()}
      {renderCancelledSubscriptions()}
      {isReferFriendsEnabled && (
        <div className={style.referFriendsWrap}>
          <ReferFriendCard content={referFriendCardContent} />
        </div>
      )}
    </Layout>
  );
};

export default Home;
